import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const ServiceContainer = styled.div`
  padding: var(--spacing-xxl) 0;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--spacing-lg);
`;

const PageTitle = styled.h1`
  margin-bottom: var(--spacing-md);
  
  span {
    color: var(--accent-turquoise);
  }
`;

const IntroText = styled.p`
  max-width: 800px;
  margin-bottom: var(--spacing-xl);
  font-size: 1.2rem;
  color: var(--text-light);
`;

const ServiceSection = styled.section`
  margin-bottom: var(--spacing-xxl);
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xl);
  margin: var(--spacing-xl) 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  background: white;
  border-radius: 8px;
  padding: var(--spacing-lg);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FeatureTitle = styled.h3`
  margin-bottom: var(--spacing-sm);
  color: var(--accent-turquoise);
`;

const CaseStudiesSection = styled.section`
  margin-top: var(--spacing-xxl);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-lg);
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.div`
  height: 200px;
  background-size: cover;
  background-position: center;
`;

const CardContent = styled.div`
  padding: var(--spacing-lg);
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  margin: var(--spacing-sm) 0;
  color: var(--text);
`;

const CardDescription = styled.p`
  color: var(--text-light);
  margin-bottom: var(--spacing-md);
`;

const CardStat = styled.div`
  font-weight: 600;
  color: var(--accent-lavender);
  margin: var(--spacing-sm) 0;
`;

const CardLink = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 20px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

const CTASection = styled.div`
  background-color: var(--secondary);
  padding: var(--spacing-xl);
  border-radius: 8px;
  text-align: center;
  margin-top: var(--spacing-xxl);
`;

const CTATitle = styled.h2`
  margin-bottom: var(--spacing-md);
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: var(--accent-turquoise);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid var(--accent-turquoise);
  transition: all 0.3s ease;
  margin-top: var(--spacing-md);
  
  &:hover {
    background-color: transparent;
    color: var(--text);
  }
`;

// Sample case studies specifically for Travel Marketing
const caseStudies = [
  {
    id: 'kikooko-seo',
    title: 'Kikooko Africa Safaris SEO',
    description: 'How targeted email campaigns and partnerships doubled bookings for this Uganda-based safari company.',
    image: '/images/projects/kikooko-seo.webp',
    keyStat: 'Email + partnerships = 2x bookings in 6 months'
  },
  {
    id: 'kikooko-social-media',
    title: 'Kikooko Social Media Revival',
    description: 'Transforming a dormant Instagram account into a thriving social media presence that drove 261% follower growth.',
    image: '/images/projects/kikooko_social_media.jpg',
    keyStat: '261% follower growth, 202% increase in website traffic'
  },
  {
    id: 'travelpayouts-email',
    title: 'Travelpayouts Email Marketing',
    description: 'Maintaining exceptional engagement rates while scaling a global travel partnership platform from 120K to 294K subscribers.',
    image: '/images/projects/travelpayouts-email.webp',
    keyStat: '29%+ open rates at scale (industry avg: 20.4%)'
  },
  {
    id: 'loving-newyork',
    title: 'Loving New York Tourism Content',
    description: 'Strategic content development that achieved top search rankings in the highly competitive NYC tourism market.',
    image: '/images/projects/loving-newyork.webp',
    keyStat: '#1 rankings for NYC tourism terms'
  }
];

const TravelMarketingService = () => {
  return (
    <ServiceContainer>
      <Container>
        <PageTitle>Travel <span>Marketing</span></PageTitle>
        <IntroText>
          Data-driven marketing strategies that connect authentic travel experiences with the right audiences. 
          Our Travel Marketing services help small to medium travel operators craft compelling stories and implement 
          effective campaigns, with specialized expertise in trade show representation and support.
        </IntroText>
        
        <ServiceSection>
          <h2>How We Help</h2>
          <p>
            We focus on sustainable growth through email marketing, content strategy, social media, 
            strategic partnerships, and comprehensive trade show support that respect your company values 
            while driving real results.
          </p>
          
          <FeatureGrid>
            <Feature>
              <FeatureTitle>Email Marketing</FeatureTitle>
              <p>Craft compelling email campaigns that tell your story and drive bookings. We've achieved open rates up to 50% (industry avg: 15-20%).</p>
            </Feature>
            <Feature>
              <FeatureTitle>Content Strategy</FeatureTitle>
              <p>Develop authentic, engaging content that resonates with your ideal audience and ranks well in search results.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Trade Show Support</FeatureTitle>
              <p>Comprehensive support for travel trade shows including marketing collateral design, lead management systems, follow-up strategies, and booth materials to maximize ROI.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Monthly Marketing Support</FeatureTitle>
              <p>Ongoing, affordable marketing assistance with clear deliverables to help you maintain consistent growth without the cost of a full-time marketing team. <Link to="/services/monthly-marketing-support">Learn more →</Link></p>
            </Feature>
            <Feature>
              <FeatureTitle>Partnership Development</FeatureTitle>
              <p>Build strategic alliances with complementary businesses to expand your reach and create new booking channels.</p>
            </Feature>
            <Feature>
              <FeatureTitle>Brand Storytelling</FeatureTitle>
              <p>Create a distinctive brand voice that communicates your values and connects emotionally with travelers.</p>
            </Feature>
          </FeatureGrid>
        </ServiceSection>
        
        <CaseStudiesSection>
          <h2>Travel Marketing Results</h2>
          <p>See how our travel marketing strategies have helped businesses like yours achieve meaningful growth.</p>
          
          <CaseStudyGrid>
            {caseStudies.map(study => (
              <Card key={study.id}>
                <Link to={`/projects/${study.id}`}>
                  <CardImage style={{ backgroundImage: `url(${study.image})` }} />
                </Link>
                <CardContent>
                  <CardTitle>{study.title}</CardTitle>
                  <CardDescription>{study.description}</CardDescription>
                  <CardStat>{study.keyStat}</CardStat>
                  <CardLink to={`/projects/${study.id}`}>View Case Study</CardLink>
                </CardContent>
              </Card>
            ))}
          </CaseStudyGrid>
        </CaseStudiesSection>
        
        <CTASection>
          <CTATitle>Ready to Amplify Your Marketing?</CTATitle>
          <p>Let's discuss how our travel marketing expertise can help your business reach the right audiences and grow bookings.</p>
          <CTAButton to="/contact">Start Your Project</CTAButton>
        </CTASection>
      </Container>
    </ServiceContainer>
  );
};

export default TravelMarketingService; 